// src/components/Boards/BoardItem.jsx
import { Link, useLocation } from 'react-router-dom';
import {
  ListItem, ListItemButton, Divider, Box,
  Typography, Chip,
} from '@mui/material';
import {
  PhotoCamera, Videocam,
  Visibility, FavoriteBorder,
} from '@mui/icons-material';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import { useUser } from '../../contexts/UserContext';
import AuthorLink from '../Common/AuthorLink';

const BoardItem = ({ post }) => {
  const location = useLocation();
  const { userInfo } = useUser();
  const isAdmin = ['admin', 'master'].includes(userInfo?.role);
  const isWholeBoard = location.pathname === '/main/board' && !location.search;

  /* ─────────── 상태 값 가공 ─────────── */
  const isDeleted = post.is_deleted;
  const isHidden = post.is_hidden;

  const MAX_TITLE = 30;
  const rawTitle = isDeleted
    ? '삭제된 게시글'
    : isHidden
      ? '블라인드된 게시글'
      : post.title;
  const trimmed = rawTitle.length > MAX_TITLE ? rawTitle.slice(0, MAX_TITLE) + '…' : rawTitle;

  const displayTitle = (!isDeleted && !isHidden && post.comment_count > 0)
    ? `${trimmed} [${post.comment_count}]`
    : trimmed;

  const displayCategory = post.category_name || '';

  const displayAuthor = post.is_anonymous
    ? (isAdmin ? `익명(${post.nickname})` : '익명')
    : post.nickname || post.actual_author || '알 수 없음';

  const displayDate = new Date(post.created_at).toLocaleString();

  const canClick = (!isDeleted && !isHidden) || isAdmin;

  /* ─────────── 렌더 ─────────── */
  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          component={canClick ? Link : 'div'}
          to={canClick ? `/main/posts/${post.id}` : undefined}
          state={{ from: location.pathname + location.search }}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1,
            px: 2,
            py: { xs: 1.5, sm: 1 },
            cursor: canClick ? 'pointer' : 'default',
          }}
        >
          {/* ───── 아이콘 영역 ───── */}
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 24 }}>
            {!isDeleted && !isHidden && (
              post.has_vote ? <HowToVoteIcon sx={{ mr: 1, color: '#2196f3' }} /> :
                post.has_video ? <Videocam sx={{ mr: 1, color: '#f44336' }} /> :
                  post.has_image ? <PhotoCamera sx={{ mr: 1, color: '#4caf50' }} /> :
                    null
            )}
          </Box>

          {/* ───── 텍스트 영역 ───── */}
          <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
            {/* ① 상태 Chip (삭제/블라인드) 또는 핀+카테고리 */}
            {isDeleted || isHidden ? (
              <Chip
                label={isDeleted ? '삭제된 게시글' : '블라인드된 게시글'}
                size="small"
                variant="outlined"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
              />
            ) : (
              <Typography
                component="div"
                sx={{
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  fontWeight: 'bold',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {(post.is_pinned_global || (!isWholeBoard && post.is_pinned)) && (
                  <Chip
                    label={post.is_pinned_global ? '📌 전체공지' : '📌 고정'}
                    size="small"
                    color="warning"
                    sx={{ mr: .5 }}
                  />
                )}
                {displayCategory && (
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    [{displayCategory}]
                  </Box>
                )}
              </Typography>
            )}

            {/* ② 글제목 (모바일 2줄, PC 1줄) */}
            {!isDeleted && !isHidden && (
              <Typography
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1rem' },

                  /* 모바일 두 줄 → …, PC 한 줄 */
                  display: { xs: '-webkit-box', sm: 'block' },
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: { xs: 2, sm: 'unset' },

                  overflow: 'hidden',
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  textOverflow: 'ellipsis',
                  overflowWrap: 'anywhere',
                }}
              >
                {displayTitle}
              </Typography>
            )}

            {/* ③ 작성자 / 날짜 (핀 게시글은 생략) */}
            {!post.is_pinned && !post.is_pinned_global && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, lineHeight: 1.4 }}
                >
                  작성자:{' '}
                  {post.is_anonymous ? (
                    /* 익명 글 → 평문만 */
                    <Box component="span">{displayAuthor}</Box>
                  ) : (
                    /* 실명 글 → 링크 가능 */
                    <AuthorLink
                      nickname={displayAuthor}
                      authorId={post.author_id}   // ✅ 추가
                      disabled={post.is_anonymous}
                    />
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, lineHeight: 1.4 }}
                >
                  날짜: {displayDate}
                  {post.is_edited && ' (수정됨)'}
                </Typography>
              </>
            )}

            {/* ④ 조회수 / 좋아요 */}
            {!isDeleted && !isHidden && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Visibility fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {post.view_count || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FavoriteBorder fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {post.like_count || 0}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </ListItemButton>
      </ListItem>

      {/* ───── 구분선 ───── */}
      <Divider
        variant="fullWidth"
        sx={{
          width: '100%',
          ml: 0,
          borderColor: 'grey.300',
          my: { xs: 1.5, sm: 0.5 },
        }}
      />
    </>
  );
};

export default BoardItem;
